import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle2, Building2, Users, Target } from 'lucide-react';
import PhoneInput from 'react-phone-number-input/input';
import { notification } from 'antd';

import { becomePartnerService } from '../../services/otherService';
import { isValidEmail } from '../../helper';

const BRAND = '#842014';
const BRAND_DARK = '#6b1a10';
const BRAND_LIGHT = '#f9c9c6';

const s = {
  section: {
    padding: '80px 0',
    backgroundColor: '#fff',
    position: 'relative',
    overflow: 'hidden',
  },
  container: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 24px',
  },
  card: {
    display: 'flex',
    flexDirection: 'row',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
    border: '1px solid #f3f4f6',
    backgroundColor: '#fff',
  },
  leftPanel: {
    position: 'relative',
    width: '41.6%',
    backgroundColor: '#0f172a',
    color: '#fff',
    padding: '48px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    overflow: 'hidden',
    flexShrink: 0,
    minHeight: '400px',
  },
  bgImage: {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
  },
  bgImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    opacity: 0.4,
    mixBlendMode: 'overlay',
  },
  bgOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(to bottom, rgba(107,26,16,0.9), rgba(15,23,42,0.95))',
  },
  leftContent: { position: 'relative', zIndex: 1 },
  leftHeading: {
    fontSize: '1.875rem',
    fontWeight: '700',
    marginBottom: '16px',
    lineHeight: 1.25,
    color: '#d1d5db',
  },
  leftSubtext: {
    color: '#d1d5db',
    marginBottom: '48px',
    fontSize: '1.125rem',
    lineHeight: 1.6,
  },
  benefitsList: { listStyle: 'none', padding: 0, margin: 0 },
  benefitItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '32px',
  },
  benefitIconWrap: {
    backgroundColor: 'rgba(255,255,255,0.1)',
    padding: '12px',
    borderRadius: '8px',
    backdropFilter: 'blur(4px)',
    color: BRAND_LIGHT,
    border: '1px solid rgba(255,255,255,0.1)',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
  },
  benefitTitle: {
    fontWeight: '600',
    fontSize: '1.125rem',
    marginBottom: '4px',
    color: '#d1d5db',
  },
  benefitDesc: {
    color: '#9ca3af',
    fontSize: '0.875rem',
    lineHeight: 1.6,
    margin: 0,
  },
  leftFooter: {
    position: 'relative',
    zIndex: 1,
    marginTop: '48px',
    paddingTop: '32px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
  },
  avatarRow: { display: 'flex', alignItems: 'center', gap: '16px' },
  avatarStack: { display: 'flex' },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    border: '2px solid #0f172a',
    backgroundColor: '#d1d5db',
    overflow: 'hidden',
    marginLeft: '-12px',
  },
  avatarFirst: { marginLeft: 0 },
  avatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
  partnerCount: { fontSize: '0.875rem', color: '#d1d5db', fontWeight: '500' },
  rightPanel: {
    flex: 1,
    padding: '64px',
    backgroundColor: '#fff',
    minWidth: 0,
  },
  formHeading: {
    fontSize: '1.875rem',
    fontWeight: '700',
    color: BRAND,
    marginBottom: '32px',
    textAlign: 'left',
  },
  successBox: {
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '12px',
    padding: '32px',
    textAlign: 'center',
  },
  successIconWrap: {
    width: '64px',
    height: '64px',
    backgroundColor: '#dcfce7',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px auto',
    color: '#16a34a',
  },
  successTitle: {
    fontSize: '1.25rem',
    fontWeight: '700',
    color: '#166534',
    marginBottom: '8px',
  },
  successMsg: { color: '#15803d', margin: 0 },
  formGrid2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
    marginBottom: '24px',
  },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
  formGroupFull: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '24px',
  },
  label: { fontSize: '0.875rem', fontWeight: '500', color: '#374151' },
  required: { color: '#ef4444' },
  input: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '8px',
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    outline: 'none',
    fontSize: '1rem',
    color: '#111827',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  inputError: {
    borderColor: '#f87171',
  },
  errorText: {
    fontSize: '0.75rem',
    color: '#ef4444',
    marginTop: '2px',
  },
  select: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '8px',
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    outline: 'none',
    fontSize: '1rem',
    color: '#111827',
    appearance: 'none',
    boxSizing: 'border-box',
    cursor: 'pointer',
  },
  textarea: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '8px',
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    outline: 'none',
    fontSize: '1rem',
    color: '#111827',
    resize: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  submitBtn: {
    backgroundColor: BRAND,
    color: '#fff',
    padding: '16px 40px',
    borderRadius: '8px',
    fontWeight: '700',
    border: 'none',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '1rem',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    transition: 'background-color 0.2s, box-shadow 0.2s, transform 0.1s',
  },
  submitBtnDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
    transform: 'none',
  },
  submitWrap: {
    paddingTop: '16px',
    display: 'flex',
  },
};

const benefits = [
  {
    icon: <Users size={20} />,
    title: 'Reach Families Directly',
    desc: 'Connect with thousands of active parents looking for opportunities.',
  },
  {
    icon: <Target size={20} />,
    title: 'Targeted Campaigns',
    desc: 'Run sweepstakes and giveaways tailored to your ideal demographic.',
  },
  {
    icon: <Building2 size={20} />,
    title: 'Brand Alignment',
    desc: 'Associate your brand with positive, community-focused activities.',
  },
];

const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
];

function FocusInput({ style, errorStyle, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      style={{
        ...s.input,
        ...(props.error ? s.inputError : {}),
        ...(focused ? { borderColor: BRAND, boxShadow: `0 0 0 3px rgba(132,32,20,0.15)` } : {}),
        ...(style || {}),
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
}

export function ContactForm() {
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', state: '', comment: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!isValidEmail(form.email)) errs.email = 'Email is not valid';
    if (!form.company.trim()) errs.company = 'Company name is required';
    if (!form.phone) errs.phone = 'Phone number is required';
    else if (form.phone.replace(/\D/g, '').length !== 11) errs.phone = 'Phone number must be exactly 10 digits';
    if (!form.state) errs.state = 'State is required';
    if (!form.comment.trim()) errs.comment = 'Please tell us how you\'d like to partner';
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setIsSubmitting(true);
    const params = {
      name: form.name,
      email: form.email,
      companyName: form.company,
      phoneNo: form.phone,
      state: form.state,
      comment: form.comment,
    };

    becomePartnerService(params).then(res => {
      setIsSubmitting(false);
      if (res?.data?.statusCode) {
        setIsSubmitted(true);
        setForm({ name: '', company: '', email: '', phone: '', state: '', comment: '' });
        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        notification.open({
          message: 'Error',
          description: 'Something went wrong. Please try again.',
        });
      }
    }).catch(() => {
      setIsSubmitting(false);
      notification.open({
        message: 'Error',
        description: 'Something went wrong. Please try again.',
      });
    });
  };

  return (
    <section style={s.section} className="cf-section">
      <div style={s.container}>
        <div style={s.card} className="cf-card">
          {/* Left Panel */}
          <div style={s.leftPanel} className="cf-left-panel">
            <div style={s.bgImage}>
              <img
                src="https://images.unsplash.com/photo-1534447677768-be436bb09401?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                alt="Night sky"
                style={s.bgImg}
              />
              <div style={s.bgOverlay} />
            </div>

            <div style={s.leftContent}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 style={s.leftHeading}>Let's Build Something Great Together</h2>
                <p style={s.leftSubtext}>
                  Join our network of partners and help us create memorable experiences for families while growing your reach.
                </p>
              </motion.div>

              <ul style={s.benefitsList}>
                {benefits.map((benefit, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.15 }}
                    style={s.benefitItem}
                  >
                    <div style={s.benefitIconWrap}>{benefit.icon}</div>
                    <div>
                      <h3 style={s.benefitTitle}>{benefit.title}</h3>
                      <p style={s.benefitDesc}>{benefit.desc}</p>
                    </div>
                  </motion.li>
                ))}
              </ul>
            </div>

            <div style={s.leftFooter}>
              <div style={s.avatarRow}>
                <div style={s.avatarStack}>
                  {[32, 44, 12, 26].map((imgId, i) => (
                    <div key={imgId} style={{ ...s.avatar, ...(i === 0 ? s.avatarFirst : {}) }}>
                      <img src={`https://i.pravatar.cc/100?img=${imgId}`} alt="Partner" style={s.avatarImg} />
                    </div>
                  ))}
                </div>
                <p style={s.partnerCount}>Join 500+ active partners</p>
              </div>
            </div>
          </div>

          {/* Right Panel */}
          <div style={s.rightPanel} className="cf-right-panel">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 style={s.formHeading}>Tell us About You</h2>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  style={s.successBox}
                >
                  <div style={s.successIconWrap}>
                    <CheckCircle2 size={32} />
                  </div>
                  <h3 style={s.successTitle}>Message Sent Successfully!</h3>
                  <p style={s.successMsg}>
                    Thank you for your interest. Our partnership team will get back to you shortly.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div style={s.formGrid2} className="cf-form-grid2">
                    <div style={s.formGroup}>
                      <label style={s.label}>Full Name <span style={s.required}>*</span></label>
                      <FocusInput
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        error={!!errors.name}
                      />
                      {errors.name && <span style={s.errorText}>{errors.name}</span>}
                    </div>
                    <div style={s.formGroup}>
                      <label style={s.label}>Company Name</label>
                      <FocusInput
                        type="text"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        placeholder="Acme Corp"
                        error={!!errors.company}
                      />
                      {errors.company && <span style={s.errorText}>{errors.company}</span>}
                    </div>
                  </div>

                  <div style={s.formGrid2}>
                    <div style={s.formGroup}>
                      <label style={s.label}>Email Address <span style={s.required}>*</span></label>
                      <FocusInput
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        error={!!errors.email}
                      />
                      {errors.email && <span style={s.errorText}>{errors.email}</span>}
                    </div>
                    <div style={s.formGroup}>
                      <label style={s.label}>Phone Number <span style={s.required}>*</span></label>
                      <PhoneInput
                        country="US"
                        placeholder="(555) 123-4567"
                        value={form.phone}
                        onChange={val => {
                          setForm(prev => ({ ...prev, phone: val || '' }));
                          if (errors.phone) setErrors(prev => ({ ...prev, phone: '' }));
                        }}
                        maxLength={16}
                        style={{ ...s.input, ...(errors.phone ? s.inputError : {}) }}
                      />
                      {errors.phone && <span style={s.errorText}>{errors.phone}</span>}
                    </div>
                  </div>

                  <div style={s.formGroupFull}>
                    <label style={s.label}>State <span style={s.required}>*</span></label>
                    <select
                      name="state"
                      value={form.state}
                      onChange={handleChange}
                      style={{ ...s.select, ...(errors.state ? s.inputError : {}) }}
                    >
                      <option value="">Select a state...</option>
                      {US_STATES.map(st => (
                        <option key={st.value} value={st.value}>{st.label}</option>
                      ))}
                    </select>
                    {errors.state && <span style={s.errorText}>{errors.state}</span>}
                  </div>

                  <div style={s.formGroupFull}>
                    <label style={s.label}>
                      How would you like to partner? <span style={s.required}>*</span>
                    </label>
                    <textarea
                      name="comment"
                      rows={4}
                      value={form.comment}
                      onChange={handleChange}
                      placeholder="Tell us about your ideas for collaboration..."
                      style={{ ...s.textarea, ...(errors.comment ? s.inputError : {}) }}
                    />
                    {errors.comment && <span style={s.errorText}>{errors.comment}</span>}
                  </div>

                  <div style={s.submitWrap}>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      style={{ ...s.submitBtn, ...(isSubmitting ? s.submitBtnDisabled : {}) }}
                    >
                      {isSubmitting ? (
                        <>
                          <svg
                            style={{ animation: 'spin 1s linear infinite', width: '20px', height: '20px' }}
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          Submit Application
                          <Send size={16} />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (max-width: 768px) {
          .cf-section { padding: 40px 0; }
          .cf-card { flex-direction: column !important; }
          .cf-left-panel {
            width: 100% !important;
            padding: 32px 24px !important;
            min-height: unset !important;
          }
          .cf-right-panel { padding: 32px 24px !important; }
          .cf-form-grid2 { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
